import { useState, useEffect, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchUsers, fetchUsersAnalytics } from "@/api/users";
import { useSessionState } from "@/hooks/useSessionState";
import { filterUsersByExpiryStatus } from "@/api/filterUsersByExpiryStatus";
import { fetchUserRoles } from "@/api/userRoles";
import {
  fetchAllActiveSubscriptions,
  fetchLatestSubscriptionsMap,
} from "@/api/user-subscriptions";
import { fetchSubscriptionPlans } from "@/api/subscription-plans";
import AdminLayout from "@/components/layout/AdminLayout";
import UserTable from "@/components/users/UserTable";
import UserFilter from "@/components/users/UserFilter";
import { User } from "@/types/index";
import { useDebounce } from "@/hooks/useDebounce";
import { usePagination } from "@/hooks/usePagination";
import {
  BarChart2,
  ChevronDown,
  ChevronUp,
  Users,
  CreditCard,
  Award,
  UserCheck,
  ArrowUpDown,
  Mail,
  Calendar,
  UserPlus,
  Clock,
  Shield,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { useNavigate } from "react-router-dom";

const UsersList = () => {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useSessionState("usersList.searchInput", "");
  const [filters, setFilters] = useSessionState("usersList.filters", {
    search: "",
    status: "",
    role: "",
    plan: "",
    expiry: "",
  });
  const [statsExpanded, setStatsExpanded] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useSessionState("usersList.currentPage", 1);
  const [pageSize, setPageSize] = useSessionState("usersList.pageSize", 10);

  // Sorting states
  const [sortColumn, setSortColumn] = useSessionState<
    | keyof User
    | "registeredAt"
    | "userRoles"
    | "subscriptionPlan"
    | "subscriptionExpiry"
  >("usersList.sortColumn", "registeredAt");
  const [sortDirection, setSortDirection] = useSessionState<"asc" | "desc">("usersList.sortDirection", "desc");

  // Debounce the search input to prevent frequent API calls while typing
  const debouncedSearchTerm = useDebounce(searchInput, 500);

  // Skip page reset on first render (preserves restored page from sessionStorage)
  const isFirstRender = useRef(true);

  // Update filters.search when debounced search term changes
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setFilters((prev) => ({ ...prev, search: debouncedSearchTerm }));
    // Ensure we always return to first page on new search
    setCurrentPage(1);
  }, [debouncedSearchTerm]);

  // Derive the filters actually sent to the server using the debounced value
  const serverFilters = useMemo(
    () => ({ ...filters, search: debouncedSearchTerm }),
    [filters, debouncedSearchTerm]
  );

  // Fetch users with pagination and filtering for the table display
  const { data: usersData, isLoading } = useQuery({
    queryKey: [
      "users",
      serverFilters,
      currentPage,
      pageSize,
      sortColumn,
      sortDirection,
    ],
    staleTime: 0,
    placeholderData: (prev) => prev,
    refetchOnMount: true,
    refetchOnReconnect: true,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      let result = await fetchUsers(
        { page: currentPage, pageSize },
        serverFilters,
        {
          column: sortColumn,
          direction: sortDirection,
        }
      );

      // Apply additional filtering for expiry status if needed
      if (serverFilters.expiry) {
        const filteredUsers = await filterUsersByExpiryStatus(
          result.data,
          serverFilters.expiry
        );

        // Update the result with filtered users
        result = {
          ...result,
          data: filteredUsers,
          total: filteredUsers.length,
          totalPages: Math.ceil(filteredUsers.length / pageSize),
        };
      }

      return result;
    },
  });

  // Fetch active subscriptions for analytics
  const { data: userActiveSubscriptionsMap } = useQuery({
    queryKey: ["userActiveSubscriptions"],
    queryFn: fetchAllActiveSubscriptions,
  });

  // Fetch latest subscriptions (any status) for table display
  const { data: latestSubscriptionsMap } = useQuery({
    queryKey: ["latestUserSubscriptions"],
    queryFn: fetchLatestSubscriptionsMap,
  });

  // Fetch subscription plans for filtering
  const { data: subscriptionPlans = [] } = useQuery({
    queryKey: ["subscriptionPlans"],
    queryFn: fetchSubscriptionPlans,
  });

  // Separate query to fetch analytics data efficiently
  const { data: analytics } = useQuery({
    queryKey: ["usersAnalytics"],
    queryFn: fetchUsersAnalytics,
  });

  // Fetch user roles for filtering
  const { data: userRoles = [] } = useQuery({
    queryKey: ["userRoles"],
    queryFn: fetchUserRoles,
  });

  const handleFilterChange = (name: string, value: string) => {
    if (name === "search") {
      // Update the search input state which will be debounced
      setSearchInput(value);
      // Don't update filters.search directly - it will be updated by the useEffect
    } else {
      // Other filters update immediately
      setFilters((prev) => ({ ...prev, [name]: value }));
      setCurrentPage(1); // Reset to first page when filters change
    }
  };

  const resetFilters = () => {
    setFilters({
      search: "",
      status: "",
      role: "",
      plan: "",
      expiry: "",
    });
    setSearchInput("");
  };

  // Handle sorting columns
  const handleSort = (
    column:
      | keyof User
      | "registeredAt"
      | "userRoles"
      | "subscriptionPlan"
      | "subscriptionExpiry"
  ) => {
    if (sortColumn === column) {
      // If clicking the same column, toggle direction
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // If clicking a different column, set it as the new sort column
      // and use appropriate default direction
      setSortColumn(column);
      if (column === "registeredAt" || column === "subscriptionExpiry") {
        setSortDirection("desc"); // Default to newest first for dates
      } else {
        setSortDirection("asc"); // Default to A-Z for text
      }
    }
  };

  // Calculate statistics for the analytics overview using the separate analytics data
  const totalUsers = analytics?.total || 0;
  const activeUsers = analytics?.active || 0;
  const pendingUsers = analytics?.pending || 0;
  const recentUsers = analytics?.recent || 0;
  const todayUsers = analytics?.today || 0;
  const yesterdayUsers = analytics?.yesterday || 0;

  // Sort roles by count
  const topRoles = Object.entries(analytics?.roles || {})
    .sort((a, b) => b[1] - a[1])
    .slice(0, 4)
    .map(([name, count]) => ({ name, count }));

  const recentUsersPercent = Math.round((recentUsers / totalUsers) * 100) || 0;

  const { totalPages, canGoPrev, canGoNext, pageNumbers } = usePagination({
    totalItems: usersData?.total || 0,
    itemsPerPage: pageSize,
    currentPage,
  });

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return (
    <AdminLayout title="Users">
      <Card className="mb-6 border border-border">
        <div
          className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-accent/30 transition-colors"
          onClick={() => setStatsExpanded(!statsExpanded)}
        >
          <div className="flex items-center">
            <div className="h-8 w-8 rounded-md bg-primary/10 flex items-center justify-center mr-3">
              <BarChart2 className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h3 className="font-medium text-base">Analytics Overview</h3>
              <p className="text-xs text-muted-foreground">
                User statistics and insights
              </p>
            </div>
            <div className="flex items-center ml-6 space-x-6 border-l border-border pl-6">
              <div className="text-center">
                <div className="text-sm font-semibold">{totalUsers}</div>
                <div className="text-xs text-muted-foreground">Total</div>
              </div>
              <div className="text-center">
                <div className="text-sm font-semibold">{activeUsers}</div>
                <div className="text-xs text-muted-foreground">Active</div>
              </div>
              <div className="text-center">
                <div className="text-sm font-semibold">{pendingUsers}</div>
                <div className="text-xs text-muted-foreground">Pending</div>
              </div>
              <div className="text-center">
                <div className="text-sm font-semibold">
                  {userActiveSubscriptionsMap?.size || 0}
                </div>
                <div className="text-xs text-muted-foreground">Subscribers</div>
              </div>
              <div className="text-center">
                <div className="text-sm font-semibold">{todayUsers}</div>
                <div className="text-xs text-muted-foreground">
                  Today so far
                </div>
              </div>
              <div className="text-center">
                <div className="text-sm font-semibold">{yesterdayUsers}</div>
                <div className="text-xs text-muted-foreground">Yesterday</div>
              </div>
              <div className="text-center">
                <div className="text-sm font-semibold">{recentUsers}</div>
                <div className="text-xs text-muted-foreground">
                  Last 30 days
                </div>
              </div>
            </div>
          </div>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
            {statsExpanded ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </Button>
        </div>

        <div
          className={cn(
            "grid grid-cols-1 md:grid-cols-4 gap-6 overflow-hidden transition-all duration-300 border-t border-border",
            statsExpanded ? "max-h-96 p-6" : "max-h-0 p-0 border-t-0"
          )}
        >
          {/* Total Users Stat */}
          <div className="bg-card rounded-lg p-4 border border-border">
            <div className="flex justify-between items-start mb-2">
              <div>
                <h4 className="text-sm font-medium text-muted-foreground">
                  Total Users
                </h4>
                <div className="text-2xl font-bold mt-1">{totalUsers}</div>
              </div>
              <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center">
                <Users className="h-5 w-5 text-primary" />
              </div>
            </div>
            <div className="flex items-center justify-between text-xs">
              <div className="text-muted-foreground">Registered users</div>
              <div className="flex items-center gap-1">
                <Badge variant="outline" className="text-xs">
                  {Math.round((activeUsers / totalUsers) * 100) || 0}% active
                </Badge>
              </div>
            </div>
          </div>

          {/* New Users Stat - NEW */}
          <div className="bg-card rounded-lg p-4 border border-border">
            <div className="flex justify-between items-start mb-2">
              <div>
                <h4 className="text-sm font-medium text-muted-foreground">
                  New Users
                </h4>
                <div className="text-2xl font-bold mt-1">{recentUsers}</div>
              </div>
              <div className="h-9 w-9 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <UserPlus className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
            <div className="relative pt-2">
              <div className="flex justify-between text-xs mb-1">
                <span className="text-muted-foreground">Last 30 days</span>
                <span className="font-medium">
                  {recentUsersPercent}% of total
                </span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className="bg-blue-500 rounded-full h-2"
                  style={{
                    width: `${recentUsersPercent}%`,
                  }}
                ></div>
              </div>
            </div>
          </div>

          {/* User Roles Distribution */}
          <div className="bg-card rounded-lg p-4 border border-border">
            <div className="flex justify-between items-start mb-3">
              <h4 className="text-sm font-medium text-muted-foreground">
                Role Distribution
              </h4>
              <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                <Shield className="h-4 w-4 text-primary" />
              </div>
            </div>
            <div className="space-y-2">
              {topRoles.map((role, index) => (
                <div
                  key={role.name}
                  className="flex items-center justify-between"
                >
                  <div className="flex items-center gap-2">
                    <div className="text-xs font-medium">{index + 1}.</div>
                    <div className="text-sm capitalize">{role.name}</div>
                  </div>
                  <div className="text-xs">
                    <Badge variant="secondary" className="text-xs font-medium">
                      {role.count}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Subscription Status - NEW */}
          <div className="bg-card rounded-lg p-4 border border-border">
            <div className="flex justify-between items-start mb-3">
              <h4 className="text-sm font-medium text-muted-foreground">
                Subscription Status
              </h4>
              <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                <CreditCard className="h-4 w-4 text-primary" />
              </div>
            </div>
            <div className="space-y-3">
              {/* Active Subscriptions */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-green-500"></div>
                    <div className="text-sm">Active</div>
                  </div>
                  <div className="text-xs">
                    <Badge
                      variant="outline"
                      className="text-xs font-medium bg-green-500/10 text-green-500 border-green-200"
                    >
                      {userActiveSubscriptionsMap?.size || 0}
                    </Badge>
                  </div>
                </div>
                <div className="h-1 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-green-500 rounded-full"
                    style={{
                      width: `${Math.round(
                        ((userActiveSubscriptionsMap?.size || 0) /
                          (totalUsers || 1)) *
                          100
                      )}%`,
                    }}
                  ></div>
                </div>
              </div>

              {/* Expired/No Subscriptions */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-gray-400"></div>
                    <div className="text-sm">No Plan</div>
                  </div>
                  <div className="text-xs">
                    <Badge
                      variant="outline"
                      className="text-xs font-medium bg-gray-500/10 text-gray-500 border-gray-200"
                    >
                      {totalUsers - (userActiveSubscriptionsMap?.size || 0)}
                    </Badge>
                  </div>
                </div>
                <div className="h-1 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gray-400 rounded-full"
                    style={{
                      width: `${
                        100 -
                        Math.round(
                          ((userActiveSubscriptionsMap?.size || 0) /
                            (totalUsers || 1)) *
                            100
                        )
                      }%`,
                    }}
                  ></div>
                </div>
              </div>

              <div className="text-xs text-muted-foreground text-center mt-1">
                {Math.round(
                  ((userActiveSubscriptionsMap?.size || 0) /
                    (totalUsers || 1)) *
                    100
                )}
                % of users have active subscriptions
              </div>
            </div>
          </div>
        </div>
      </Card>

      <Card className="mb-6 bg-card text-card-foreground border-border">
        <CardContent className="py-4">
          <UserFilter
            filters={filters}
            searchInput={searchInput}
            onFilterChange={handleFilterChange}
            onResetFilters={resetFilters}
            userRoles={userRoles}
            subscriptionPlans={subscriptionPlans}
          />
        </CardContent>
      </Card>

      <Card className="bg-card text-card-foreground border-border">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : usersData?.data.length === 0 ? (
          <div className="text-center py-12 bg-muted/30 rounded-lg mx-4 mb-4">
            <p className="text-muted-foreground mb-2">No users found</p>
            <p className="text-sm text-muted-foreground">
              Try adjusting your search or filter criteria
            </p>
          </div>
        ) : (
          <>
            <div className="rounded-md border border-border">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-muted/50">
                    <TableHead
                      className={cn(
                        "cursor-pointer hover:bg-muted/50 transition-colors",
                        sortColumn === "name" && "bg-muted"
                      )}
                      onClick={() => handleSort("name")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>User</span>
                        {sortColumn === "name" && (
                          <ArrowUpDown
                            className={`h-4 w-4 ${
                              sortDirection === "asc"
                                ? "transform rotate-180"
                                : ""
                            }`}
                          />
                        )}
                      </div>
                    </TableHead>
                    <TableHead
                      className={cn(
                        "cursor-pointer hover:bg-muted/50 transition-colors",
                        sortColumn === "isActive" && "bg-muted"
                      )}
                      onClick={() => handleSort("isActive")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Status</span>
                        {sortColumn === "isActive" && (
                          <ArrowUpDown
                            className={`h-4 w-4 ${
                              sortDirection === "asc"
                                ? "transform rotate-180"
                                : ""
                            }`}
                          />
                        )}
                      </div>
                    </TableHead>
                    <TableHead
                      className={cn(
                        "cursor-pointer hover:bg-muted/50 transition-colors",
                        sortColumn === "subscriptionPlan" && "bg-muted"
                      )}
                      onClick={() => handleSort("subscriptionPlan")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Plan</span>
                        {sortColumn === "subscriptionPlan" && (
                          <ArrowUpDown
                            className={`h-4 w-4 ${
                              sortDirection === "asc"
                                ? "transform rotate-180"
                                : ""
                            }`}
                          />
                        )}
                      </div>
                    </TableHead>
                    <TableHead
                      className={cn(
                        "cursor-pointer hover:bg-muted/50 transition-colors",
                        sortColumn === "subscriptionExpiry" && "bg-muted"
                      )}
                      onClick={() => handleSort("subscriptionExpiry")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Expiry</span>
                        {sortColumn === "subscriptionExpiry" && (
                          <ArrowUpDown
                            className={`h-4 w-4 ${
                              sortDirection === "asc"
                                ? "transform rotate-180"
                                : ""
                            }`}
                          />
                        )}
                      </div>
                    </TableHead>
                    <TableHead
                      className={cn(
                        "cursor-pointer hover:bg-muted/50 transition-colors",
                        sortColumn === "userRoles" && "bg-muted"
                      )}
                      onClick={() => handleSort("userRoles")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Role</span>
                        {sortColumn === "userRoles" && (
                          <ArrowUpDown
                            className={`h-4 w-4 ${
                              sortDirection === "asc"
                                ? "transform rotate-180"
                                : ""
                            }`}
                          />
                        )}
                      </div>
                    </TableHead>
                    <TableHead
                      className={cn(
                        "cursor-pointer hover:bg-muted/50 transition-colors",
                        sortColumn === "registeredAt" && "bg-muted"
                      )}
                      onClick={() => handleSort("registeredAt")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Registered</span>
                        {sortColumn === "registeredAt" && (
                          <ArrowUpDown
                            className={`h-4 w-4 ${
                              sortDirection === "asc"
                                ? "transform rotate-180"
                                : ""
                            }`}
                          />
                        )}
                      </div>
                    </TableHead>
                    <TableHead className="text-right">
                      <div className="flex items-center justify-end">
                        <span></span>
                      </div>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <UserTable
                    users={usersData.data}
                    userRoles={userRoles}
                    userSubscriptions={
                      latestSubscriptionsMap || userActiveSubscriptionsMap
                    }
                    activeUserSubscriptions={userActiveSubscriptionsMap}
                  />
                </TableBody>
              </Table>
            </div>

            {/* Pagination Controls */}
            <div className="flex items-center justify-between px-4 py-4 border-t border-border">
              <div className="text-sm text-muted-foreground">
                Showing{" "}
                <span className="font-medium">
                  {usersData?.total === 0
                    ? 0
                    : (currentPage - 1) * pageSize + 1}
                </span>{" "}
                to{" "}
                <span className="font-medium">
                  {Math.min(currentPage * pageSize, usersData?.total || 0)}
                </span>{" "}
                of <span className="font-medium">{usersData?.total || 0}</span>{" "}
                filtered users
                {usersData?.total !== analytics?.total && (
                  <span> (total: {analytics?.total || 0})</span>
                )}
              </div>
              <div className="flex items-center gap-4">
                {totalPages > 0 && (
                  <div className="flex items-center">
                    <Button
                      className="mr-1 px-2"
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(1)}
                      disabled={!canGoPrev}
                    >
                      First
                    </Button>

                    <Button
                      className="mr-1 px-2"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setCurrentPage(currentPage > 1 ? currentPage - 1 : 1)
                      }
                      disabled={!canGoPrev}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>

                    {pageNumbers.map((pageNum) => (
                      <Button
                        key={pageNum}
                        className="mx-0.5 w-8 h-8 p-0"
                        variant={
                          pageNum === currentPage ? "default" : "outline"
                        }
                        size="sm"
                        onClick={() => setCurrentPage(pageNum)}
                      >
                        {pageNum}
                      </Button>
                    ))}

                    <Button
                      className="ml-1 px-2"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setCurrentPage(
                          currentPage < totalPages
                            ? currentPage + 1
                            : totalPages
                        )
                      }
                      disabled={!canGoNext}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>

                    <Button
                      className="ml-1 px-2"
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={!canGoNext}
                    >
                      Last
                    </Button>
                  </div>
                )}

                <select
                  className="h-9 rounded-md border border-border bg-background px-2 text-sm"
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1); // Reset to first page when changing page size
                  }}
                >
                  {[10, 20, 30, 50].map((size) => (
                    <option key={size} value={size}>
                      {size} per page
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </>
        )}
      </Card>
    </AdminLayout>
  );
};

export default UsersList;
